import React, { useEffect, useState, useCallback } from 'react';

import { Container, DataTable, Loading, ErrorMessage } from '@/src/ui';
import { SimpleTextField } from '@/src/ui/SimpleForm';
import { apiRequest } from '@/src/services';
import { ReportHeaderStyle, ReportCellStyle } from '@/src/utils';

export interface S3File {
    key: string;
    url: string;
    isFolder: boolean;
}

const fetchS3Files = async (prefix: string = ''): Promise<S3File[]> => {
    const url = prefix ? `/s3files?prefix=${encodeURIComponent(prefix)}` : '/s3files';
    const response = await apiRequest('GET', url);
    if (response.status < 200 || response.status >= 300) throw new Error('Failed to load S3 files');
    return response.data as S3File[];
};

const NightlySyncPage: React.FC = () => {
    document.title = 'Nightly Sync';

    const [files, setFiles] = useState<(S3File & { date?: string })[]>([]);
    const [searchString, setSearchString] = useState('');
    const [currentPrefix, setCurrentPrefix] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [breadcrumbs, setBreadcrumbs] = useState<string[]>([]);

    const loadFiles = useCallback(async (prefix: string = '') => {
        setLoading(true);
        setError('');
        try {
            const data = await fetchS3Files(prefix);
            // Add date property to each file (if not folder)
            const filesWithDate = data.map((file) => {
                if (!file.isFolder) {
                    // Extract date from filename (last part before .csv)
                    const match = file.key.match(/_(\d{8})\.csv$/);
                    if (match) {
                        // Format as YYYY-MM-DD
                        const raw = match[1];
                        const formatted = `${raw.slice(4, 8)}-${raw.slice(0, 2)}-${raw.slice(2, 4)}`;
                        return { ...file, date: formatted };
                    }
                }
                return { ...file, date: '' };
            });
            setFiles(filesWithDate);
            setCurrentPrefix(prefix);
            setBreadcrumbs(prefix ? prefix.split('/').filter(Boolean) : []);
        } catch (err: any) {
            setError(err.message || 'Failed to load files');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadFiles();
    }, [loadFiles]);

    const handleOpenFolder = (folderKey: string) => {
        loadFiles(folderKey);
    };

    const handleBreadcrumbClick = (index: number) => {
        const prefix = breadcrumbs.slice(0, index + 1).join('/') + '/';
        loadFiles(prefix);
    };

    const columns = [
        {
            dataKey: 'key',
            header: 'Name',
            align: 'left' as const,
            allowSort: true,
            emptyValue: '-',
            headerSx: ReportHeaderStyle,
            cellSx: ReportCellStyle,
            formatter: (row: S3File & { date?: string }) =>
                row.isFolder ? (
                    <button
                        className="text-blue-600 underline"
                        onClick={() => handleOpenFolder(row.key)}
                    >
                        {row.key.replace(currentPrefix, '')}
                    </button>
                ) : (
                    <a
                        className="text-green-700 underline"
                        href={`https://us-west-2.console.aws.amazon.com/s3/object/paradigm-bulkupload-district-csvs?region=us-west-2&prefix=${encodeURIComponent(row.key)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {row.key.replace(currentPrefix, '')}
                    </a>
                ),
        },
        {
            dataKey: 'date',
            header: 'Date',
            align: 'center' as const,
            allowSort: true,
            emptyValue: '-',
            headerSx: ReportHeaderStyle,
            cellSx: ReportCellStyle,
            formatter: (row: S3File & { date?: string }) => row.date || '',

            sortMethod: (a: string, b: string, sortOrder: 'asc' | 'desc') => {
                if (!a) return 1;
                if (!b) return -1;
                return sortOrder === 'asc' ? a.localeCompare(b) : b.localeCompare(a);
            },
        },
        {
            dataKey: 'isFolder',
            header: 'Type',
            align: 'center' as const,
            allowSort: true,
            emptyValue: '-',
            headerSx: ReportHeaderStyle,
            cellSx: ReportCellStyle,
            formatter: (row: S3File) => (row.isFolder ? 'Folder' : 'File'),
        },
    ];

    // Filter files by search string
    const filteredFiles = files.filter((file) => {
        const search = searchString.trim().toLowerCase();
        if (!search) return true;
        return file.key.toLowerCase().includes(search);
    });

    return (
        <Container>
            <h1 className="page-header">Nightly Sync S3 Files</h1>
            <br />
            <div className="flex items-center mb-4 gap-4">
                <SimpleTextField
                    name="search"
                    label="Search files"
                    value={searchString}
                    onChange={(e) => setSearchString(e.target.value)}
                    variant="outlined"
                    sx={{ maxWidth: 400 }}
                />
            </div>
            {breadcrumbs.length > 0 && (
                <div className="mb-4 flex gap-2 text-sm">
                    <button className="text-blue-500 underline" onClick={() => loadFiles('')}>
                        Root
                    </button>
                    {breadcrumbs.map((crumb, idx) => (
                        <span key={idx}>
                            {' / '}
                            <button
                                className="text-blue-500 underline"
                                onClick={() => handleBreadcrumbClick(idx)}
                            >
                                {crumb}
                            </button>
                        </span>
                    ))}
                </div>
            )}
            <Loading visible={loading} message="Loading S3 files..." />
            <ErrorMessage error={error} />
            {!loading && !error && (
                <DataTable
                    data={filteredFiles}
                    columns={columns}
                    tableName="S3 Files"
                    emptyMessage="No files or folders found."
                    rowsPerPageInitial={20}
                    alternatingRowColor
                    defaultSortColumn="date"
                    defaultSortDirection="desc"
                />
            )}
        </Container>
    );
};

export default NightlySyncPage;
